/**
 * The three renting types, and the seasons rule that decides which one a pair
 * of dates can be (the user's scheme, 2026-08-18; day rule relaxed 2026-08-21):
 *
 *   dagverhuur      one calendar day, the whole year round
 *   weekendverhuur  overnight stay in the school year (sep–jun)
 *   kampverhuur     overnight stay in the summer (jul–aug); 2 extra rooms and
 *                   more showers come with it
 *
 * The type is DERIVED from the dates, never chosen freely. A single summer day
 * is dagverhuur, not "a camp" (user call — "we might as well rent it out").
 * Stated trade-off: nothing prioritises camps yet, so a day let on 14 July can
 * block a camp week first-come-first-served. The intended fix is a booking
 * horizon (kamp reservable far ahead, dag/weekend only close by) — carried on
 * Q15 in the backlog, NOT implemented here. This rule lives here so the price
 * engine, the form and the seed cannot disagree about what a stay is.
 */

import { compareDates, nightsBetween, type PlainDate } from "./time.js";

export const VERHUURTYPES = ["dag", "weekend", "kamp"] as const;
export type Verhuurtype = (typeof VERHUURTYPES)[number];

/** July and August: the building's own camp season. */
export function isZomer(date: PlainDate): boolean {
  return date.month === 7 || date.month === 8;
}

export type VerhuurtypeResult =
  | { readonly ok: true; readonly verhuurtype: Verhuurtype }
  | {
      readonly ok: false;
      /**
       * geen_kamp_in_schooljaar  — a long stay outside jul–aug is not offered
       * seizoensgrens            — the stay starts in one season, ends in the
       *                            other; which price would even apply?
       * ongeldig                 — departure before arrival
       */
      readonly reason: "geen_kamp_in_schooljaar" | "seizoensgrens" | "ongeldig";
    };

/** How long a school-year stay may run before it stops being "a weekend". */
const MAX_WEEKEND_NIGHTS = 4;

/**
 * Which renting type a stay is, or why it is not offered.
 *
 * Worked examples:
 *   wo 14 okt → wo 14 okt   = dag
 *   wo 15 jul → wo 15 jul   = dag (a one-day let is never "a camp")
 *   vr 2 okt → zo 4 okt     = weekend (2 nachten)
 *   vr 10 jul → ma 20 jul   = kamp (10 nachten)
 *   vr 2 okt → vr 9 okt     = geen_kamp_in_schooljaar (7 nachten past niet
 *                             in een weekend en kampen zijn enkel in de zomer)
 */
export function verhuurtypeVoor(arrival: PlainDate, departure: PlainDate): VerhuurtypeResult {
  if (compareDates(departure, arrival) < 0) return { ok: false, reason: "ongeldig" };
  const nights = nightsBetween(arrival, departure);

  // A single calendar day never crosses a season and is dagverhuur the whole
  // year round (user call, 2026-08-21). Until the Q15 booking horizon exists,
  // this is first-come-first-served against camp weeks — a stated trade-off.
  if (nights === 0) return { ok: true, verhuurtype: "dag" };

  // The night before departure is the last night actually slept; a stay
  // departing on 1 July never touches the summer season.
  const lastNight = departure.subtract({ days: 1 });
  if (isZomer(arrival) !== isZomer(lastNight)) return { ok: false, reason: "seizoensgrens" };

  if (isZomer(arrival)) return { ok: true, verhuurtype: "kamp" };
  if (nights <= MAX_WEEKEND_NIGHTS) return { ok: true, verhuurtype: "weekend" };
  return { ok: false, reason: "geen_kamp_in_schooljaar" };
}
